import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, MousePointerClick } from "lucide-react";
import { API } from "@/config";
import { Card } from "@/components/admin/ui";

/**
 * "Cancel anytime ✨" ribbon click-through analytics.
 *
 * Compact insights card showing how the PoM ribbon is performing vs the
 * Add-to-booking button. Only the top 5 products by total interaction show
 * so the tile stays glanceable on the Dashboard.
 */
export const PomRibbonStats = ({ days = 30 }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let ok = true;
    axios.get(`${API}/admin/analytics/pom-ribbon`, { params: { days } })
      .then((r) => { if (ok) setData(r.data); })
      .catch(() => { if (ok) setData({ totals: { ribbon_clicks: 0, add_clicks: 0 }, by_product: [] }); });
    return () => { ok = false; };
  }, [days]);

  const totals = data?.totals || { ribbon_clicks: 0, add_clicks: 0 };
  const top = (data?.by_product || []).slice(0, 5);
  const overallCtr = totals.add_clicks
    ? Math.round((totals.ribbon_clicks / totals.add_clicks) * 100)
    : 0;

  return (
    <Card>
      <div className="p-5" data-testid="pom-ribbon-stats">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#FFD166]" />
            <h2 className="font-serif text-xl text-[#FFD166]">PoM ribbon · last {days} days</h2>
          </div>
          <div className="flex gap-4 text-xs">
            <div>
              <span className="uppercase tracking-wider text-[#B9C8D5]">Ribbon</span>{" "}
              <span className="text-[#FFD166] font-medium tabular-nums" data-testid="ribbon-total-clicks">{totals.ribbon_clicks}</span>
            </div>
            <div>
              <span className="uppercase tracking-wider text-[#B9C8D5]">Adds</span>{" "}
              <span className="text-emerald-300 font-medium tabular-nums" data-testid="add-total-clicks">{totals.add_clicks}</span>
            </div>
            <div>
              <span className="uppercase tracking-wider text-[#B9C8D5]">Ratio</span>{" "}
              <span className="text-[#F6F1E4] font-medium">{overallCtr}%</span>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[#B9C8D5] mt-1">
          How many shoppers clicked the "Cancel anytime ✨" ribbon vs went straight to Add. Higher ribbon-to-add ratio = copy is landing.
        </p>

        {!data ? (
          <div className="mt-6 text-sm text-[#B9C8D5]">Loading…</div>
        ) : top.length === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center text-center gap-1 py-6 text-[#B9C8D5] text-sm" data-testid="ribbon-stats-empty">
            <MousePointerClick className="w-8 h-8 opacity-40" />
            <div>No ribbon interactions yet — go shop your own site to seed data!</div>
          </div>
        ) : (
          <div className="mt-5 space-y-2" data-testid="ribbon-stats-rows">
            {top.map((r) => {
              const total = (r.ribbon_clicks || 0) + (r.add_clicks || 0);
              const ribbonPct = total ? (r.ribbon_clicks / total) * 100 : 0;
              return (
                <div key={r.product_id} className="rounded-lg border border-[#FFD166]/15 bg-black/25 p-3" data-testid={`ribbon-stats-row-${r.product_id}`}>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <div className="truncate text-[#F6F1E4]">{r.product_id}</div>
                    <div className="text-[11px] text-[#B9C8D5] shrink-0 tabular-nums">
                      <span className="text-[#FFD166]">{r.ribbon_clicks}</span> ribbon
                      {" · "}
                      <span className="text-emerald-300">{r.add_clicks}</span> add
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-black/40 overflow-hidden flex">
                    <div className="bg-[#FFD166] h-full" style={{ width: `${ribbonPct}%` }} />
                    <div className="bg-emerald-400 h-full" style={{ width: `${100 - ribbonPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PomRibbonStats;
